import { useMemo } from "react";
import { format, isSameDay, getDay, getISOWeek } from "date-fns";
import { getHoliday } from "../../utils/holidays";
import { de } from "date-fns/locale";
import { VISIBLE_DAYS_BUFFER, ZOOM_LEVELS, GRID_BG, GRID_CELL_BG } from "./constants";
import DayCell from "./DayCell";

export default function PlanningGrid({
  visibleDays, weekGroups, displayUsers, today,
  activeCalendarWeek, activeYear,
  scrollContainerRef, headerScrollRef, scrollbarWidth,
  handleScroll, handleWheel, handleGrabStart, isGrabbing,
  getAssignmentsForCell, getTotalHours, hasDayBlockedStatus,
  handleDragStart, handleDragEnd, handleDragOver, handleDrop,
  handleResizeStart, handleFillHandleStart,
  dragOverDate, dragOverUserId, dropValidation,
  resizingAssignment, resizePreviewHours,
  modifiedIds, newIds, deletedIds,
  showSollIst, isFillDragging, fillPreviewCells,
  selectedCell, selectedIds,
  handleAddClick, handleAssignmentClick, handleCellSelect,
  handleSelectAssignment, handleQuickDelete,
  loading, assignmentsLength,
  zoom = "standard",
  onOpenInTimesheet,
}) {
  const zoomConfig = ZOOM_LEVELS[zoom] || ZOOM_LEVELS.standard;
  const dayWidth = zoomConfig.dayWidth;
  const rowHeight = zoomConfig.rowHeight;
  const empColWidth = 150;
  // Endlos-Scroll: Gesamtbreite richtet sich nach tatsächlich sichtbaren Tagen,
  // nicht mehr nach festem VISIBLE_DAYS_BUFFER (21). Die Days-Anzahl wächst dynamisch.
  const totalGridWidth = empColWidth + visibleDays.length * dayWidth;

  if (loading && assignmentsLength === 0) {
    return (
      <div className="flex-1 flex justify-center items-center rounded-xl" style={{ background: GRID_BG }}>
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-7 w-7 border-[3px] border-t-transparent" style={{ borderColor: "var(--tga-blue)", borderTopColor: "transparent" }} />
          <span className="text-[12px] font-medium" style={{ color: "var(--tga-text-muted)" }}>Lade Einsatzplan...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex-1 flex flex-col overflow-hidden rounded-xl"
      style={{
        background: GRID_BG,
        border: "1px solid var(--tga-border)",
        boxShadow: "0 4px 24px var(--tga-shadow-overlay, rgba(0,0,0,0.2)), inset 0 1px 0 rgba(255,255,255,0.02)",
      }}
    >
      {/* SINGLE scroll container for everything */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-auto select-none"
        style={{ cursor: isGrabbing ? "grabbing" : "grab" }}
        onScroll={handleScroll}
        onWheel={handleWheel}
        onMouseDown={handleGrabStart}
        data-testid="planning-grid"
        data-grid-area="true"
      >
        <div style={{ width: totalGridWidth, minHeight: "100%" }}>

          {/* ─── STICKY HEADER ─── */}
          <div
            className="flex"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 20,
              borderBottom: "2px solid var(--tga-border)",
            }}
          >
            {/* Corner cell: sticky left + top */}
            <div
              className="flex-shrink-0 flex items-center justify-center"
              style={{
                width: empColWidth,
                minHeight: 56,
                background: "var(--tga-bg-elevated)",
                borderRight: "2px solid var(--tga-border)",
                position: "sticky",
                left: 0,
                zIndex: 30,
                boxShadow: "2px 0 8px rgba(0,0,0,0.08)",
              }}
            >
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "var(--tga-text-dim)" }}>
                Mitarbeiter
              </span>
            </div>

            {/* Day headers */}
            <div style={{ display: "flex", flexDirection: "column", width: visibleDays.length * dayWidth }}>
              {/* Week Labels */}
              <div className="flex" style={{ borderBottom: "1px solid var(--tga-border)" }}>
                {weekGroups.map((week, idx) => (
                  <div
                    key={`wk-${idx}`}
                    className="flex-shrink-0 px-3 py-1.5 text-[11px] font-bold"
                    style={{
                      width: week.days.length * dayWidth,
                      borderRight: "2px solid var(--tga-border)",
                      background: week.isActiveWeek ? "rgba(59,130,246,0.06)" : "var(--tga-bg-elevated)",
                      color: week.isActiveWeek ? "#60A5FA" : "var(--tga-text-dim)",
                    }}
                  >
                    KW {week.weekNum} {format(week.monday, "MMM yyyy", { locale: de })}
                  </div>
                ))}
              </div>
              {/* Day Labels */}
              <div className="flex">
                {visibleDays.map((day) => {
                  const isTodayDay = isSameDay(day, today);
                  const dow = getDay(day);
                  const isWe = dow === 6 || dow === 0;
                  const dateStr = format(day, "yyyy-MM-dd");
                  const holiday = getHoliday(dateStr);
                  let hBg = "var(--tga-bg-elevated)";
                  if (isWe) hBg = "var(--tga-bg-app)";
                  if (holiday) hBg = "rgba(245, 158, 11, 0.10)";
                  if (isTodayDay) hBg = "var(--tga-blue-soft)";

                  return (
                    <div
                      key={day.toISOString()}
                      className="flex-shrink-0 text-center py-1.5 relative"
                      style={{
                        width: dayWidth,
                        background: hBg,
                        borderRight: dow === 0 ? "2px solid var(--tga-border)" : "1px solid var(--tga-border)",
                      }}
                      title={holiday ? `${holiday.name}${holiday.bundeseinheitlich ? " (bundesweit)" : " (regional)"}` : undefined}
                      data-testid={`ep-day-header-${dateStr}`}
                    >
                      <div
                        className="text-[10px] uppercase font-semibold"
                        style={{ color: holiday ? "#FBBF24" : isWe ? "var(--tga-bg-active)" : "var(--tga-text-dim)" }}
                      >
                        {format(day, "EEE", { locale: de })}
                      </div>
                      <div
                        className="text-[14px] font-bold"
                        style={{ color: isTodayDay ? "#60A5FA" : holiday ? "#FBBF24" : isWe ? "var(--tga-bg-active)" : "var(--tga-text-primary)" }}
                      >
                        {format(day, "d")}
                      </div>
                      {holiday && (
                        <div
                          className="text-[8px] font-semibold leading-tight px-1 truncate"
                          style={{ color: "#FCD34D" }}
                          data-testid={`ep-holiday-label-${dateStr}`}
                        >
                          {holiday.name}
                        </div>
                      )}
                      {isTodayDay && <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: "var(--tga-blue)" }} />}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ─── DATA ROWS ─── */}
          <div className="relative">
            {/* Today vertical line */}
            {visibleDays.map((day, idx) => {
              if (!isSameDay(day, today)) return null;
              return (
                <div
                  key="today-line"
                  className="absolute pointer-events-none"
                  style={{
                    left: empColWidth + idx * dayWidth + dayWidth / 2,
                    top: 0,
                    width: 1,
                    height: displayUsers.length * rowHeight,
                    background: "rgba(59,130,246,0.10)",
                    zIndex: 1,
                  }}
                />
              );
            })}

            {displayUsers.map((u) => (
              <div key={u.id} className="flex" style={{ height: rowHeight }}>
                {/* Sticky Employee Cell */}
                <div
                  className="flex-shrink-0 px-3 py-1 flex flex-col justify-center"
                  style={{
                    width: empColWidth,
                    height: rowHeight,
                    background: "var(--tga-bg-elevated)",
                    borderRight: "2px solid var(--tga-border)",
                    borderBottom: "1px solid var(--tga-border)",
                    position: "sticky",
                    left: 0,
                    zIndex: 10,
                    boxShadow: "2px 0 8px rgba(0,0,0,0.08)",
                  }}
                >
                  <div className="font-bold text-[13px] truncate" style={{ color: "var(--tga-text-bright)" }}>
                    {u.name}
                  </div>
                  <div className="text-[10px] mt-0.5" style={{ color: "var(--tga-text-dim)" }}>
                    {u.role}
                  </div>
                </div>

                {/* Day Cells */}
                {visibleDays.map((day) => {
                  const isTodayDay = isSameDay(day, today);
                  const cellAssignments = getAssignmentsForCell(u.id, day);
                  const totalHours = getTotalHours(u.id, day);
                  const hasBlocked = hasDayBlockedStatus(u.id, day);
                  const dow = getDay(day);
                  const cellHoliday = getHoliday(format(day, "yyyy-MM-dd"));

                  return (
                    <DayCell
                      key={day.toISOString()}
                      date={day} userId={u.id}
                      assignments={cellAssignments} totalHours={totalHours}
                      onAddClick={handleAddClick} onAssignmentClick={handleAssignmentClick}
                      onCellSelect={handleCellSelect}
                      onDragStart={handleDragStart} onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver} onDrop={handleDrop}
                      onResizeStart={handleResizeStart} onFillHandleStart={handleFillHandleStart}
                      isWeekendDay={dow === 6 || dow === 0} isSaturday={dow === 6} isSunday={dow === 0}
                      isToday={isTodayDay} isWeekEnd={dow === 0}
                      isHoliday={!!cellHoliday} holidayName={cellHoliday?.name || null}
                      dragOverDate={dragOverDate} dragOverUserId={dragOverUserId}
                      isValidDrop={dropValidation.valid}
                      resizingAssignment={resizingAssignment} resizePreviewHours={resizePreviewHours}
                      modifiedIds={modifiedIds} newIds={newIds} deletedIds={deletedIds}
                      hasBlockedStatus={hasBlocked} showSollIst={showSollIst}
                      isFillDragging={isFillDragging}
                      fillPreviewCell={fillPreviewCells.find(c => isSameDay(c.date, day) && c.userId === u.id)}
                      isSelected={selectedCell && isSameDay(selectedCell.date, day) && selectedCell.userId === u.id}
                      selectedIds={selectedIds}
                      onSelectAssignment={handleSelectAssignment}
                      onQuickDelete={handleQuickDelete}
                      onOpenInTimesheet={onOpenInTimesheet}
                      dayWidth={dayWidth}
                      rowHeight={rowHeight}
                      zoom={zoom}
                    />
                  );
                })}
              </div>
            ))}

            {displayUsers.length === 0 && (
              <div className="p-4 text-center text-[12px]" style={{ color: "var(--tga-text-dim)" }}>
                Keine Mitarbeiter
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
